import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { format } from 'date-fns';
import FilterTable, { BaseData } from '../components/Table/Table';
import { useGetCustomersQuery, useDeleteCustomerMutation, useGetCustomerGroupsQuery } from '../store';
import { CustomerDTO } from '../models/customer';
import { formatCurrency } from '../utils/shared';
import AdminSectionTitle from '../components/AdminSectionTitle';
import ViewEditCustomer from '../components/ViewEditCustomer';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import MenuDropdown from '../components/atoms/MenuDropdown';
import CustomModal from '../components/atoms/CustomModal';
import Button from '../components/atoms/Button';
import { IconPlay2 } from '../components/icons';
import { toastSuccess } from '../utils/toasts';

interface TableCustomerItem extends BaseData {
  name: string | JSX.Element;
  email: string | JSX.Element;
  phone: string | JSX.Element;
  person_id: string | JSX.Element;
  last_purchase_at: string | JSX.Element;
  total_tickets_purchased: number | JSX.Element;
  total_spent: number | JSX.Element;
  groups: string | JSX.Element;
}

const AdminCustomersPage: React.FC = () => {
  const { business_id: businessId } = useParams<{ business_id: string }>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'view' | 'edit' | 'delete'>('view');
  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const { data: customerGroups } = useGetCustomerGroupsQuery({ businessId: businessId || '' });
  const [deleteCustomer, { isLoading: isDeleting, isSuccess: isDeleted }] = useDeleteCustomerMutation();

  console.log(customerGroups);
  console.log(customer);

  if (!businessId) {
    throw new Error('invalid business id');
  }

  const { data: customers, isFetching } = useGetCustomersQuery({ businessId });

  useEffect(() => {
    if (isDeleted) {
      setIsModalOpen(false);
      toastSuccess('Cliente eliminado correctamente');
    }
  }, [isDeleted]);

  const headCells = [
    { id: 'name', label: 'Nombre' },
    { id: 'groups', label: 'Grupos' },
    { id: 'email', label: 'Email' },
    { id: 'phone', label: 'Teléfono' },
    { id: 'person_id', label: 'Documento' },
    { id: 'last_purchase_at', label: 'Última compra' },
    { id: 'total_tickets_purchased', label: 'Entradas' },
    { id: 'total_spent', label: 'Gastado' },
  ] as const;

  const rows: TableCustomerItem[] = useMemo(() => (customers || []).map((c: CustomerDTO) => ({
    id: c.id,
    name: <div className='whitespace-nowrap'>{`${c.first_name || ''} ${c.last_name || ''}`.trim() || '-'}</div>,
    email: <div className='whitespace-nowrap'>{c.email || '-'}</div>,
    phone: <div className='whitespace-nowrap'>{c.phone || '-'}</div>,
    person_id: <div className='whitespace-nowrap'>{c.person_id || '-'}</div>,
    last_purchase_at: c.last_purchase_at ? <div className='whitespace-nowrap'>{format(new Date(c.last_purchase_at), 'dd/MM/yyyy HH:mm')}</div> : '-',
    total_tickets_purchased: <div className='whitespace-nowrap'>{c.total_tickets_purchased || 0}</div>,
    total_spent: c.total_spent ? <div className='whitespace-nowrap'>{formatCurrency(c.total_spent, 'no-decimals')}</div> : 0,
    groups: <div className='whitespace-nowrap'>{c.customer_groups.map((g) => g.name).join(', ') || '-'}</div>,
  })), [customers]);

  const dropDownOptions = [
    {
      label: 'Ver cliente',
      onClick: (row: CustomerDTO) => {
        const customerRow = customers?.find((c) => c.id === row.id);
        if (!customerRow) {
          throw new Error('Customer not found');
        }
        setModalType('view');
        setCustomer(customerRow);
        setIsModalOpen(true);
      },
    },
    {
      label: 'Editar cliente',
      onClick: (row: CustomerDTO) => {
        const customerRow = customers?.find((c) => c.id === row.id);
        if (!customerRow) {
          throw new Error('Customer not found');
        }
        setModalType('edit');
        setCustomer(customerRow);
        setIsModalOpen(true);
      },
    },
    {
      label: 'Eliminar cliente',
      onClick: (row: CustomerDTO) => {
        setModalType('delete');
        setCustomer(row);
        setIsModalOpen(true);
      },
    },
  ];

  const actions = (row: TableCustomerItem) => {
    const customerRow = customers?.find((c) => c.id === row.id);
    if (!customerRow) {
      throw new Error('Customer not found');
    }
    return (
      <MenuDropdown<CustomerDTO>
        options={dropDownOptions}
        data={customerRow}
      />
    );
  };

  return (
    <div className='flex flex-col gap-4 my-6'>
      <AdminSectionTitle
        title="Productoras"
        subtitle='CRM - Clientes'
        icon={<IconPlay2 className='w-12 h-12 rotate-180' />}
      />

      <div className="glass-surface">
        <h1 className="text-2xl font-medium">Clientes</h1>
        {isFetching && <OrdersTableSkeleton />}
        {!isFetching && (
          <FilterTable
            rows={rows}
            headCells={headCells}
            pagination={{ pageSize: 30, enabled: true }}
            searchEnabled
            tableId="customers-table"
            filterColumns
            actions={actions}
          />
        )}
      </div>
      <CustomModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        hideCancelButton
      >
        <>
          {(modalType === 'view' || modalType === 'edit') && (
            <div className='flex flex-col gap-4 w-[60vw] max-w-lg'>
              {customer && <ViewEditCustomer customer={customer} onClose={() => setIsModalOpen(false)} editMode={modalType === 'edit'} customerGroups={customerGroups || []} />}
            </div>
          )}
          {modalType === 'delete' && (
            <div className='flex flex-col gap-4 w-[60vw] max-w-lg'>
              <div className='flex flex-col gap-2'>
                <h1>Eliminar cliente</h1>
                <p>¿Estás seguro de querer eliminar este cliente?</p>
                <div className='flex gap-2'>
                  <Button variant='secondary' onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                  <Button variant='primary' onClick={() => deleteCustomer({ businessId, customerId: customer?.id || '' })} loading={isDeleting}>Eliminar</Button>
                </div>
              </div>
            </div>
          )}
        </>
      </CustomModal>
    </div>
  );
};

export default AdminCustomersPage;
